import debounce from '../../../lib/utils/debounce';
import { dataDelayServiceHit } from '../../../store/params';
import { showAlert } from '../../../lib/helpers';

export default {
  data() {
    return {
      bcko: null,
      cmer: null,
      rmReferalCode: '',
      searchBcko: '',
      searchCmer: '',
      debounceSearchBcko: null,
      debounceSearchCmer: null,
      lastBckoKeywordRequested: null,
      lastCmerKeywordRequested: null,
      bckoListLoaded: false,
      cmerListLoaded: false,
    };
  },
  computed: {
    itemsBcko() {
      const list = this.bckoListLoaded ? this.$store.state.bcko.allData : [];
      return this.withSelectedItem(list, this.bcko, 'branch_code_bcko');
    },
    itemsCmer() {
      const list = this.cmerListLoaded ? this.$store.state.cmer.allData : [];
      return this.withSelectedItem(list, this.cmer, 'code');
    },
    isLoadingBcko() {
      return this.$store.state.bckoShared.loading;
    },
    isLoadingCmer() {
      return this.$store.state.cmerShared.loading;
    },
  },
  mounted() {
    this.debounceSearchBcko = debounce((keyword) => {
      this.fetchBckoData(keyword).catch(() => {});
    }, dataDelayServiceHit);
    this.debounceSearchCmer = debounce((keyword) => {
      this.fetchCmerData(keyword).catch(() => {});
    }, dataDelayServiceHit);
  },
  watch: {
    searchBcko: {
      handler(val) {
        if (!this.bckoListLoaded) {
          return;
        }
        if (this.isBckoSelectionSearch(val)) {
          return;
        }
        this.debounceSearchBcko(val);
      },
    },
    searchCmer: {
      handler(val) {
        if (!this.cmerListLoaded) {
          return;
        }
        if (this.isCmerSelectionSearch(val)) {
          return;
        }
        this.debounceSearchCmer(val);
      },
    },
  },
  methods: {
    // v-autocomplete hanya bisa menampilkan v-model-nya kalau object tersebut
    // ada di dalam `items`. List master BCKO/CMER cuma memuat 10 baris pertama,
    // jadi item terpilih harus selalu ikut disisipkan ke items - kalau tidak,
    // field tampil kosong walaupun v-model-nya terisi.
    withSelectedItem(list, selected, codeKey) {
      const items = list || [];
      if (!selected) {
        return items;
      }

      const exists = items.some(
        (item) =>
          (item.id != null &&
            selected.id != null &&
            String(item.id) === String(selected.id)) ||
          (item[codeKey] != null && item[codeKey] === selected[codeKey])
      );

      return exists ? items : [selected, ...items];
    },
    parseRegionalLabel(value) {
      if (typeof value !== 'string') {
        return { code: '', label: '' };
      }

      const normalized = value.trim();
      if (!normalized) {
        return { code: '', label: '' };
      }

      const parts = normalized.split(' - ');
      const code = parts[0] ? parts[0].trim() : normalized;
      const label = parts.length > 1 ? parts.slice(1).join(' - ').trim() : '';

      return { code, label };
    },
    setRegionalMappingFromDetail() {
      const bckoLabel = this.data?.bcko || '';
      const cmerLabel = this.data?.cmer || '';
      const referralCode = this.data?.referral_code || '';

      const parsedBcko = this.parseRegionalLabel(bckoLabel);
      const parsedCmer = this.parseRegionalLabel(cmerLabel);

      const bckoId = this.data?.bcko_id || this.data?.branch_code_bcko || parsedBcko.code || '';
      const cmerId = this.data?.cmer_id || this.data?.code || parsedCmer.code || '';

      const selectedBcko = this.itemsBcko.find(
        (item) =>
          String(item.id) === String(bckoId) ||
          item.branch_code_bcko === bckoId ||
          item.branch_code_bcko === parsedBcko.code
      );
      const selectedCmer = this.itemsCmer.find(
        (item) =>
          String(item.id) === String(cmerId) ||
          item.code === cmerId ||
          item.code === parsedCmer.code
      );

      const hasBckoData = bckoId !== '' || bckoLabel !== '';
      const fallbackBcko = hasBckoData
        ? {
          id: bckoId || bckoLabel,
          branch_code_bcko: parsedBcko.code || bckoId || bckoLabel,
          outlet_name: parsedBcko.label || '',
        }
        : null;

      this.bcko = selectedBcko || fallbackBcko;

      const hasCmerData = cmerId !== '' || cmerLabel !== '';
      const fallbackCmer = hasCmerData
        ? {
          id: cmerId || cmerLabel,
          code: parsedCmer.code || cmerId || cmerLabel,
          definition: parsedCmer.label || '',
        }
        : null;

      this.cmer = selectedCmer || fallbackCmer;
      this.rmReferalCode = referralCode;
    },
    // List master BCKO/CMER baru diambil saat user benar-benar membuka
    // field-nya. Saat halaman dibuka, nilai field murni berasal dari detail,
    // jadi tidak ada response list yang bisa menimpanya.
    async onFocusBcko() {
      if (this.bckoListLoaded) {
        return;
      }
      this.bckoListLoaded = true;

      try {
        await this.fetchBckoData('');
      } catch (error) {
        this.bckoListLoaded = false;
        showAlert(
          this.$store,
          'Failed to load BKCO data. Please try again.',
          'mdi-alert',
          'red'
        );
      }
    },
    async onFocusCmer() {
      if (this.cmerListLoaded) {
        return;
      }
      this.cmerListLoaded = true;

      try {
        await this.fetchCmerData('');
      } catch (error) {
        this.cmerListLoaded = false;
        showAlert(
          this.$store,
          'Failed to load CMER data. Please try again.',
          'mdi-alert',
          'red'
        );
      }
    },
    async fetchBckoData(keyword = '') {
      const searchKeyword = typeof keyword === 'string' ? keyword.trim() : '';

      if (
        searchKeyword === this.lastBckoKeywordRequested &&
        (this.isLoadingBcko ||
          (this.$store.state.bcko.allData || []).length > 0)
      ) {
        return;
      }

      this.lastBckoKeywordRequested = searchKeyword;

      const result = await this.$store.dispatch('bckoShared/getBcko', {
        page: 1,
        keyword: searchKeyword,
      });

      if (!result?.success) {
        throw new Error(result?.message || 'Failed to load BKCO data');
      }

      this.$store.commit('bcko/setData', {
        payload: {
          data: {
            list: result.data || [],
          },
        },
      });
      this.$store.commit('bcko/setTotalData', {
        payload: {
          data: {
            countdata: result.total,
            totalPage: 1,
          },
        },
      });
    },
    async fetchCmerData(keyword = '') {
      const searchKeyword = typeof keyword === 'string' ? keyword.trim() : '';

      if (
        searchKeyword === this.lastCmerKeywordRequested &&
        (this.isLoadingCmer ||
          (this.$store.state.cmer.allData || []).length > 0)
      ) {
        return;
      }

      this.lastCmerKeywordRequested = searchKeyword;

      const result = await this.$store.dispatch('cmerShared/getCmer', {
        page: 1,
        keyword: searchKeyword,
      });

      if (!result?.success) {
        throw new Error(result?.message || 'Failed to load CMER data');
      }

      this.$store.commit('cmer/setData', {
        payload: {
          data: {
            list: result.data || [],
          },
        },
      });
      this.$store.commit('cmer/setTotalData', {
        payload: {
          data: {
            countdata: result.total,
            totalPage: 1,
          },
        },
      });
    },
    isBckoSelectionSearch(value) {
      if (!this.bcko || typeof value !== 'string') {
        return false;
      }

      const searchValue = value.trim().toLowerCase();
      if (!searchValue) {
        return false;
      }

      const code = String(this.bcko.branch_code_bcko || '').toLowerCase();
      const name = String(this.bcko.outlet_name || '').toLowerCase();
      const label = `${code} - ${name}`.trim();

      return searchValue === code || searchValue === name || searchValue === label;
    },
    isCmerSelectionSearch(value) {
      if (!this.cmer || typeof value !== 'string') {
        return false;
      }

      const searchValue = value.trim().toLowerCase();
      if (!searchValue) {
        return false;
      }

      const code = String(this.cmer.code || '').toLowerCase();
      const definition = String(this.cmer.definition || '').toLowerCase();
      const label = `${code} - ${definition}`.trim();

      return (
        searchValue === code || searchValue === definition || searchValue === label
      );
    },
  },
};